import React, { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import PropTypes from 'prop-types';
import { getClassName } from '../../../utils/InputValidation';
import withScopedTranslations from '../../../withScopedTranslations/withScopedTranslations';

const MonthPicker = (props) => {
  const ref = useRef(null);

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setShowDatePicker(false);
    }
  };

  useEffect(() => {
    setMonths(getMonths(year));
    document.addEventListener('click', handleClickOutside, true);
    if (props.input.value) {
      const [y, m] = props.input.value.split('-').map(Number);
      onDateClick({ index: m - 1, year: y, });
    }
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [props.input.value, props.tReady]);

  const getMonths = (year) => {
    const months = [...Array(12).keys()].map((i) => ({ month: monthMap[i], year, index: i, }));
    return months;
  };

  const oneDay = 60 * 60 * 24 * 1000;
  const todayTimestamp = Date.now() - (Date.now() % oneDay) + (new Date().getTimezoneOffset() * 1000 * 60);
  const date = new Date();
  const initYear = date.getFullYear();
  const [input, setInput] = useState('');
  const [year, setYear] = useState(initYear);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [months, setMonths] = useState();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const monthMap = props.t('months', { returnObjects: true, })
  const { touched, invalid, visited, } = props.meta;
  const isCurrentMonth = (month) => {
    const today = new Date(todayTimestamp);
    return month.index === today.getMonth() && month.year === today.getFullYear();
  };

  const isSelectedMonth = (month) => {
    return month.index === selectedMonth.index && month.year === selectedMonth.year;
  };

  const onDateClick = (month) => {
    const index = (month.index + 1 > 10) ? month.index + 1 : `0${month.index + 1}`;
    setSelectedMonth(month);
    setDateToInput(month);
    props.input.onChange([month.year, index, '01'].join('-'));
    setShowDatePicker(false);
  };

  const setDateToInput = (month) => {
    const index = month.index < 9 ? `0${month.index + 1}`  : month.index + 1
    setInput(`${index}/${month.year}`);
  };

  const hanleSetYear = (offset) => {
    const newYear = parseInt(year, 10) + offset;
    setYear(newYear);
    setMonths(getMonths(newYear));
  };
  const highlightMonth = (month) => {
    return selectedMonth ? isSelectedMonth(month) : isCurrentMonth(month);
  };
  const classes = useStyles();
  const renderMonths = () => months.map((month) => {
    return (
      <div className={classes.singleMonthContainer} key={month.month + month.year}>
        <div className={classes.singleMonth}>
          <div
            onClick={() => onDateClick(month)}
            className={`${classes.monthName} ${highlightMonth(month) ? classes.monthNameChecked : ''}`}
          >
            {month.month}
          </div>
        </div>
      </div>
    );
  });
  return (
    <div className={classes.monthPicker}>
      <div onClick={() => setShowDatePicker(true)} className="input-wrapper">
        <input type="text" readOnly="readonly" className={getClassName(touched, invalid, visited)} value={input} />
      </div>
      {showDatePicker ? (
        <div
          className={classes.calendarContainer}
          style={props.wrapedStyle}
          ref={ref}
        >
          <div className={classes.headContainer}>
            <span
              onClick={() => hanleSetYear(-1)}
              className={classes.arrow}
            />
            <div className={classes.year}>{year}</div>
            <span
              className={classes.arrow}
              onClick={() => hanleSetYear(1)}
              style={{ transform: 'rotate(180deg)', }}
            />
          </div>
          <div className={classes.monthsContainer}>
            {renderMonths()}
          </div>
        </div>
      ) : ''}
    </div>
  );
};

const useStyles = createUseStyles({
  monthPicker: {
    position: 'relative',
  },
  calendarContainer: {
    position: 'absolute',
    zIndex: '2',
    backgroundColor: 'white',
    maxWidth: '340px',
    boxShadow: '1px 3px 3px 3px rgba( 0, 0, 0, 0.2)',
    minWidth: '300px',
    paddingTop: '1em',
    borderRadius: '2%',
    animation: 'slideIn 350ms ease-out 0s 1 forwards',
    top: '-5em',
  },
  arrow: {
    display: 'inline-block',
    cursor: 'pointer',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: '#57C2F0',
    color: '#FFFFFF',
    '&:after': {
      content: '\'\'',
      display: 'inline-block',
      width: '0.9em',
      height: '0.9em',
      marginTop: '0.63em',
      marginLeft: '10px',
      borderTop: '0.33em solid #FFFFFF',
      borderRight: '0.33em solid #FFFFFF',
      '-moz-transform': 'rotate(-135deg)',
      '-webkit-transform': 'rotate(-135deg)',
      transform: 'rotate(-135deg)',
    },
  },
  headContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    margin: '1em',
    paddingBottom: '1.5em',
  },
  year: {
    fontSize: '18px',
    fontWeight: '200',
  },
  monthsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: '14px',
    marginBottom: '4em',
  },
  singleMonthContainer: {
    width: '33%',
  },
  singleMonth: {
    cursor: 'pointer',
    textAlign: 'center',
    marginTop: '1em',
    display: 'flex',
    justifyContent: 'space-around',
  },
  monthName: {
    padding: '4px 6px',
    borderRadius: '28%',
    '&:hover': {
      backgroundColor: '#e6e6e6',
      opacity: '90%',
    },
  },
  monthNameChecked: {
    backgroundColor: '#57C2F0',
    color: 'white',
  },
});

MonthPicker.propTypes = {
  input: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  tReady: PropTypes.bool.isRequired,
  wrapedStyle: PropTypes.object,
  meta: PropTypes.object.isRequired,
};
MonthPicker.defaultProps = {
  wrapedStyle: null,
};

export default withScopedTranslations('ad_vehicle_description')(MonthPicker);
